import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { connect } from 'react-redux';
import { uniqBy, find } from 'lodash';
import 'url-search-params-polyfill';
import Modal from '../../Modal/ReactBootstrapModal';
import { getApiAxiosInstance } from '../../../utils/network';

import { getMemberDashboardResults } from '../../../actions/dashboard-actions';
import { getMyGroupMembers } from '../../../api/playerGroupApi';
import { showErrorPopup } from '../../../actions/notification-actions';
import { isValidEmail } from '../../../utils/validation';
import {
    filterPlayers,
    findPlayer,
    sortPlayersByRating,
    mapMembers,
} from '../../../utils/createPlay';
import TabBar from '../../TabBar';
import PlayerResultsTab from './PlayerResultsTab';
import PlayerPill from './PlayerPill';
import styles from './playerInviteModal.scss';
import { sportTypeIds } from '../../../utils/eventConstants';

const _ = { uniqBy, find };

const TabIdentifiers = {
    PLAYERS: 1,
    MY_GROUP: 2,
    PAST_OPPONENTS: 3,
};

const PlayerInviteModal = (props) => {
    const {
        isCreatePlay,
        getMemberResults,
        invitedEmails,
        invitedMembers,
        isVisible,
        onApplyClicked,
        onGroupMembersSet,
        onOpponentMembersSet,
        onRequestClose,
        auth,
        dashboard,
        removedGroupMemberId,
        removedOpponentMemberId,
        sportTypeId = sportTypeIds.TENNIS,
    } = props;
    const { recentResults = [] } = dashboard.newDashboard;
    const resultLimit = 10;
    const cancelRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [query, setQuery] = useState('');
    const [groupQuery, setGroupQuery] = useState('');
    const [opponentsQuery, setOpponentsQuery] = useState('');
    const [offset, setOffset] = useState(0);
    const [isEnd, setIsEnd] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [selectedEmails, setSelectedEmails] = useState(invitedEmails || []);
    const [selectedPlayers, setSelectedPlayers] = useState(invitedMembers || []);
    const [playerGroupMembers, setPlayerGroupMembers] = useState([]);
    const [originalGroupMembers, setOriginalGroupMembers] = useState([]);
    const [playerOpponentMembers, setPlayerOpponentMembers] = useState([]);
    const [originalOpponentMembers, setOriginalOpponentMembers] = useState([]);
    const [previousInvites, setPreviousInvites] = useState({ ids: [], emails: [] });
    const [tabs, setTabs] = useState([
        {
            id: TabIdentifiers.PLAYERS,
            title: 'Players',
            showTab: !isCreatePlay,
            active: !isCreatePlay,
        },
        {
            id: TabIdentifiers.MY_GROUP,
            title: isCreatePlay ? 'My Player Group' : 'My Group',
            showTab: true,
            active: isCreatePlay,
        },
        {
            id: TabIdentifiers.PAST_OPPONENTS,
            title: 'My Past Opponents',
            showTab: isCreatePlay,
            active: false,
        },
    ]);
    const activeTab = _.find(tabs, { active: true });
    const isPlayerTabActive = activeTab?.id === TabIdentifiers.PLAYERS;
    const isMyGroupTabActive = activeTab?.id === TabIdentifiers.MY_GROUP;
    const isMyPastOpponentsTabActive = activeTab?.id === TabIdentifiers.PAST_OPPONENTS;

    const isApplyButtonDisabled = [...selectedEmails, ...selectedPlayers].length === 0;

    useEffect(() => {
        if (removedGroupMemberId) {
            const groupPlayerToBeAddedBack = findPlayer(originalGroupMembers, removedGroupMemberId);
            const sortedGroupMembers = sortPlayersByRating([...playerGroupMembers, groupPlayerToBeAddedBack]);
            setPlayerGroupMembers(sortedGroupMembers);
        }
    }, [removedGroupMemberId]);

    useEffect(() => {
        if (!isCreatePlay) {
            return;
        }

        const opponentPlayerToBeAddedBack = findPlayer(originalOpponentMembers, removedOpponentMemberId);
        const sortedOpponentMembers = sortPlayersByRating([...playerOpponentMembers, opponentPlayerToBeAddedBack]);
        setPlayerOpponentMembers(sortedOpponentMembers);
    }, [removedOpponentMemberId]);

    useEffect(() => {
        const getGroupMembers = async () => {
            const axiosInstance = getApiAxiosInstance();
            const page = 1;
            const playerGroupResultLimit = 30;
            const response = await getMyGroupMembers(axiosInstance, page, playerGroupResultLimit);
            if (response.data.error) {
                props.showErrorPopup({
                    message: 'Error loading My Player Group',
                });
            } else if (response.data) {
                const { results } = response.data;
                const groupMembers = mapMembers(results);

                setPlayerGroupMembers(groupMembers);
                setOriginalGroupMembers(groupMembers);
                onGroupMembersSet(groupMembers);
            }
        };

        getGroupMembers();
    }, []);

    useEffect(() => {
        if (!isCreatePlay) {
            return;
        }

        if (!recentResults.length) {
            getMemberResults();
        }

        let opponentPlayers = [];
        recentResults.forEach((result) => {
            opponentPlayers.push(result.players.winner1);
            opponentPlayers.push(result.players.loser1);
            if (result.winner2) {
                opponentPlayers.push(result.players.winner2);
            }
            if (result.loser2) {
                opponentPlayers.push(result.players.loser2);
            }
        });
        const { id: currentMemberId } = auth.user;
        opponentPlayers = opponentPlayers.filter(player => player.memberId !== currentMemberId);
        opponentPlayers = _.uniqBy(opponentPlayers, 'id');
        const opponentMembers = mapMembers(opponentPlayers);
        setPlayerOpponentMembers(opponentMembers);
        setOriginalOpponentMembers(opponentMembers);
        onOpponentMembersSet(opponentMembers);
    }, [recentResults.length]);

    useEffect(() => {
        const invitedPlayerEmails = invitedEmails.map(player => player?.email);
        const invitedPlayerIds = invitedMembers.map(player => player?.source?.id);
        const updatedSelectedEmails = selectedEmails.reduce((updatedEmails, player) => {
            if (invitedPlayerEmails.includes(player.email)) {
                updatedEmails.push(player);
            }
            return updatedEmails;
        }, []);
        const updatedSelectedPlayers = selectedPlayers.reduce((updatedPlayers, player) => {
            if (invitedPlayerIds.includes(player?.source?.id)) {
                updatedPlayers.push(player);
            }
            return updatedPlayers;
        }, []);

        setPreviousInvites({ ids: invitedPlayerIds, emails: invitedPlayerEmails });
        setSelectedEmails(updatedSelectedEmails);
        setSelectedPlayers(updatedSelectedPlayers);
    }, [invitedEmails, invitedMembers]);

    useEffect(() => {
        const getSearchResults = async () => {
            const axiosInstance = getApiAxiosInstance();
            const params = new URLSearchParams();

            params.append('query', query);
            params.append('top', resultLimit);
            params.append('skip', offset);
            params.append('exists', 'memberId');

            const cancel = cancelRef.current;
            if (cancel) {
                cancel();
            }
            const response = await axiosInstance.get('v2/search/players', {
                cancelToken: new CancelToken(((c) => {
                    cancelRef.current = c;
                })),
                params,
            }).catch((error) => {
                if (!isCancel(error)) {
                    setIsLoading(false);
                }
            });

            if (response?.data) {
                const { hits } = response.data;
                const results = offset ? [...searchResults, ...hits] : hits;
                if (selectedPlayers.length) {
                    const filteredResults = results.filter(player => !selectedPlayers.some(selectedPlayer => player.source.id === selectedPlayer.source.id));
                    setSearchResults(filteredResults);
                } else {
                    setSearchResults(results);
                }

                setIsLoading(false);
                setIsEnd(hits.length < resultLimit);
                setOffset(offset + resultLimit);
            }
        };

        if (isLoading && query.length > 0) {
            getSearchResults();
        }
    }, [isLoading, query]);

    const hasValidEmailAddress = (email) => {
        let error = null;
        if (!isValidEmail(email)) {
            error = 'Please enter a valid email address';
        }
        setEmailError(error);

        return error === null;
    };

    const updateStateForQuery = (queryString) => {
        if (queryString.length > 0) {
            if (queryString.includes('@')) {
                setIsLoading(false);
                setQuery(queryString);
                setSearchResults([]);
            } else {
                setIsLoading(true);
                setQuery(queryString);
                setSearchResults([]);
                setOffset(0);
                setIsEnd(false);
            }
        } else {
            setQuery('');
            setSearchResults([]);
            setOffset(0);
            setIsEnd(false);
            setEmailError(null);
        }
    };

    const clearState = () => {
        setIsLoading(false);
        setSearchResults([]);
        setQuery('');
        setOffset(0);
        setIsEnd(false);
        setGroupQuery('');
        setOpponentsQuery('');
    };

    const resetGroupPlayers = () => {
        const selectedPlayerIds = selectedPlayers.map(player => player.source.id);
        const unselectedGroupPlayers = originalGroupMembers.reduce((updatedPlayers, player) => {
            if (!selectedPlayerIds.includes(player.source.id)) {
                updatedPlayers.push(player);
            }
            return updatedPlayers;
        }, []);
        setPlayerGroupMembers(unselectedGroupPlayers);
    };

    const resetOpponentPlayers = () => {
        const selectedPlayerIds = selectedPlayers.map(player => player.source.id);
        const unselectedOpponentPlayers = originalOpponentMembers.reduce((updatedPlayers, player) => {
            if (!selectedPlayerIds.includes(player.source.id)) {
                updatedPlayers.push(player);
            }
            return updatedPlayers;
        }, []);
        setPlayerOpponentMembers(unselectedOpponentPlayers);
    };

    const handleOnPlayerSearchInputChange = (event) => {
        if (emailError) {
            setEmailError(null);
        }
        const queryString = event.target.value;
        updateStateForQuery(queryString);
    };

    const handleMyGroupInputChange = (event) => {
        if (emailError) {
            setEmailError(null);
        }
        const queryString = event.target.value;
        setGroupQuery(queryString);

        if (queryString) {
            const filteredResults = originalGroupMembers.filter(
                player => player.source.displayName.toLocaleUpperCase().includes(queryString.toLocaleUpperCase()),
            );
            setPlayerGroupMembers(filteredResults);
        } else {
            resetGroupPlayers();
            setEmailError(null);
        }
    };

    const handleMyPastOpponentsInputChange = (event) => {
        if (emailError) {
            setEmailError(null);
        }
        const queryString = event.target.value;
        setOpponentsQuery(queryString);

        if (queryString) {
            const filteredResults = originalOpponentMembers.filter(
                player => player.source.displayName.toLocaleUpperCase().includes(queryString.toLocaleUpperCase()),
            );
            setPlayerOpponentMembers(filteredResults);
        } else {
            resetOpponentPlayers();
            setEmailError(null);
        }
    };

    const handleOnKeyDown = (event) => {
        if (event.key === 'Enter') {
            // adding by email is possible only on players tab
            if (!isPlayerTabActive) {
                return;
            }

            const trimmedEmail = query.trim();
            const emailInvite = { email: trimmedEmail };
            const hasValidEmail = hasValidEmailAddress(trimmedEmail);
            if (hasValidEmail) {
                setSelectedEmails([...selectedEmails, emailInvite]);
                if (isPlayerTabActive) {
                    setQuery('');
                }
                if (isMyGroupTabActive) {
                    setGroupQuery('');
                }
                if (isMyPastOpponentsTabActive) {
                    setOpponentsQuery('');
                }
            }
        }
    };

    const handleOnInputPaste = (event) => {
        const { target } = event;
        setTimeout(() => {
            const queryString = target.value.trim();
            updateStateForQuery(queryString);
        }, 200);
    };

    const handleOnRequestClose = () => {
        onRequestClose();
        clearState();
    };

    const handleOnApplyClicked = () => {
        onApplyClicked(selectedPlayers, selectedEmails);
        onRequestClose();
        clearState();
    };

    const handleOnClearPlayerSearchInput = () => {
        clearState();
        setEmailError(null);
    };

    const handleOnMyGroupClearInput = () => {
        setGroupQuery('');
        setEmailError(null);
        resetGroupPlayers();
    };

    const handleOnMyPastOpponentsClearInput = () => {
        setOpponentsQuery('');
        setEmailError(null);
        resetOpponentPlayers();
    };

    const handleOnTabClicked = (ev, selectedTabId) => {
        const updatedTabs = tabs.map((tab) => {
            return { ...tab, active: tab.id === selectedTabId };
        });
        if (isPlayerTabActive) {
            setGroupQuery('');
            setOpponentsQuery('');
        } else {
            clearState();
        }
        setTabs(updatedTabs);
    };

    const handleOnPlayerSelected = (playerId) => {
        const isGroupMember = playerGroupMembers.some(member => member.source.id === playerId);
        const isOpponentMember = playerOpponentMembers.some(member => member.source.id === playerId);

        if (isGroupMember) {
            const selectedGroupMember = findPlayer(playerGroupMembers, playerId);
            setSelectedPlayers([...selectedPlayers, selectedGroupMember]);
            const updatedGroupMembers = filterPlayers(playerGroupMembers, playerId);
            setPlayerGroupMembers(updatedGroupMembers);
        } else if (isOpponentMember) {
            const selectedOpponentMember = findPlayer(playerOpponentMembers, playerId);
            setSelectedPlayers([...selectedPlayers, selectedOpponentMember]);
            const updatedOpponentMembers = filterPlayers(playerOpponentMembers, playerId);
            setPlayerOpponentMembers(updatedOpponentMembers);
        } else {
            const selectedPlayer = findPlayer(searchResults, playerId);
            setSelectedPlayers([...selectedPlayers, selectedPlayer]);
        }

        const updatedSearchResults = filterPlayers(searchResults, playerId);
        setSearchResults(updatedSearchResults);
    };

    const handleOnRemovePlayer = (removedPlayer) => {
        const isGroupMember = originalGroupMembers.some(member => member.source.id === removedPlayer.source.id);
        const isOpponentMember = originalOpponentMembers.some(member => member.source.id === removedPlayer.source.id);
        if (isGroupMember) {
            const updatedGroupMembers = [...playerGroupMembers, removedPlayer];
            const sortedResults = sortPlayersByRating(updatedGroupMembers);
            setPlayerGroupMembers(sortedResults);

            const updatedSelectedPlayers = filterPlayers(selectedPlayers, removedPlayer.source.id);
            setSelectedPlayers(updatedSelectedPlayers);
        } else if (isOpponentMember) {
            const updatedOpponentMembers = [...playerOpponentMembers, removedPlayer];
            const sortedResults = sortPlayersByRating(updatedOpponentMembers);
            setPlayerOpponentMembers(sortedResults);

            const updatedSelectedPlayers = filterPlayers(selectedPlayers, removedPlayer.source.id);
            setSelectedPlayers(updatedSelectedPlayers);
        } else {
            const updatedSelectedPlayers = filterPlayers(selectedPlayers, removedPlayer.source.id);
            setSelectedPlayers(updatedSelectedPlayers);

            const updatedSearchResults = [...searchResults, removedPlayer];
            const sortedResults = sortPlayersByRating(updatedSearchResults);
            setSearchResults(sortedResults);
        }
    };

    const handleOnRemoveEmail = (removedEmail) => {
        const updatedSelectedEmails = selectedEmails.filter(invite => invite.email !== removedEmail.email);
        setSelectedEmails(updatedSelectedEmails);
    };

    const handleOnLoadMoreItems = () => {
        if (!isLoading && !isEnd) {
            setIsLoading(true);
        }
    };

    const getSearchInputForTab = () => {
        if (isPlayerTabActive && !isCreatePlay) {
            return (
                <>
                    <input
                        type="text"
                        value={query}
                        className="form-control"
                        onChange={handleOnPlayerSearchInputChange}
                        onPaste={handleOnInputPaste}
                        placeholder={'Search player name or add by email address'}
                        onKeyDown={handleOnKeyDown}
                    />
                    {!!query.length && (
                        <span
                            className={`form-control-feedback fas fa-times-circle ${styles.feedbackRight}`}
                            onClick={handleOnClearPlayerSearchInput}
                        />
                    )}
                </>
            );
        }

        if (isMyGroupTabActive) {
            return (
                <>
                    <input
                        type="text"
                        value={groupQuery}
                        className="form-control"
                        onChange={handleMyGroupInputChange}
                        placeholder={'Search player name in my group'}
                        onKeyDown={handleOnKeyDown}
                    />
                    {!!groupQuery.length && (
                        <span
                            className={`form-control-feedback fas fa-times-circle ${styles.feedbackRight}`}
                            onClick={handleOnMyGroupClearInput}
                        />
                    )}
                </>
            );
        }

        if (isMyPastOpponentsTabActive) {
            return (
                <>
                    <input
                        type="text"
                        value={opponentsQuery}
                        className="form-control"
                        onChange={handleMyPastOpponentsInputChange}
                        placeholder={'Search player name in past opponents'}
                        onKeyDown={handleOnKeyDown}
                    />
                    {!!opponentsQuery.length && (
                        <span
                            className={`form-control-feedback fas fa-times-circle ${styles.feedbackRight}`}
                            onClick={handleOnMyPastOpponentsClearInput}
                        />
                    )}
                </>
            );
        }

        return (<></>);
    };

    return (
        <Modal visible={isVisible}>
            <div className="modal-header">
                <div className="close-icon" onClick={handleOnRequestClose}><i className="utr utr-close" /></div>
                <button
                    type="button"
                    className={`btn btn-primary btn-large`}
                    onClick={handleOnApplyClicked}
                    disabled={isApplyButtonDisabled}
                >
                    APPLY
                </button>
            </div>
            <div className="modal-body">
                <div className={styles.globalSearchContainer}>
                    <div className={styles.globalSearchInputContainer}>
                        {getSearchInputForTab()}
                    </div>
                </div>
                {emailError && <div className={styles.error}>{emailError}</div>}
                <div className={styles.pillContainer}>
                    {selectedPlayers.map(player => {
                        const isPreviousInvite = previousInvites.ids.includes(player.source.id);
                        return (
                            <PlayerPill
                                key={player.source.id}
                                player={player}
                                showRemoveButton={!isPreviousInvite}
                                onRemovePlayer={handleOnRemovePlayer}
                            />
                        );
                    })}
                    {selectedEmails.map(player => {
                        const isPreviousInvite = previousInvites.emails.includes(player.email);
                        return (
                            <PlayerPill
                                key={player.email}
                                emailInvite={player}
                                showRemoveButton={!isPreviousInvite}
                                onRemoveEmail={handleOnRemoveEmail}
                            />
                        );
                    })}
                </div>
                <TabBar
                    tabs={tabs}
                    handleTabClick={handleOnTabClicked}
                    showLabels={true}
                    centerTabs={false}
                    clickCategory="Player Invite"
                    clickLocation="Player Invite Modal"
                    data-testid="modal.player-invite.tab-bar"
                />
                <div className={isPlayerTabActive ? 'd-block' : 'd-none'}>
                    <PlayerResultsTab
                        isEndOfSearch={isEnd}
                        isActiveSearch={query.length > 0}
                        isLoading={isLoading}
                        isAddingEmail={query.includes('@')}
                        playerResults={searchResults}
                        onLoadMoreItems={handleOnLoadMoreItems}
                        onPlayerSelected={handleOnPlayerSelected}
                    />
                </div>
                <div className={isMyGroupTabActive ? 'd-block' : 'd-none'}>
                    <>
                        <PlayerResultsTab
                            isEndOfSearch={true}
                            isActiveSearch={false}
                            isLoading={false}
                            isAddingEmail={false}
                            playerResults={playerGroupMembers}
                            onPlayerSelected={handleOnPlayerSelected}
                            sportTypeId={sportTypeId}
                        />
                        {isCreatePlay && !originalGroupMembers.length && (
                            <div>
                                Create your event and share the link, and as players confirm they will be added to this group.
                            </div>
                        )}
                    </>
                </div>
                {isCreatePlay && (
                    <div className={isMyPastOpponentsTabActive ? 'd-block' : 'd-none'}>
                        <PlayerResultsTab
                            isEndOfSearch={true}
                            isActiveSearch={false}
                            isLoading={false}
                            isAddingEmail={false}
                            playerResults={playerOpponentMembers}
                            onPlayerSelected={handleOnPlayerSelected}
                            sportTypeId={sportTypeId}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};

PlayerInviteModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    invitedMembers: PropTypes.instanceOf(Array).isRequired,
    invitedEmails: PropTypes.instanceOf(Array).isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onApplyClicked: PropTypes.func.isRequired,
    showErrorPopup: PropTypes.func.isRequired,
};

function mapDispatchToProps(dispatch) {
    return {
        getMemberResults: () => dispatch(getMemberDashboardResults()),
        showErrorPopup: (data) => dispatch(showErrorPopup(data)),
    };
}

function mapStateToProps({ auth, playerGroups, dashboard }) {
    return { auth, playerGroups, dashboard };
}

export default connect(mapStateToProps, mapDispatchToProps)(PlayerInviteModal);
